import { API_ROUTES, formatMinor, searchHitSchema, type SearchHit } from '@hvac/contracts';
import { z } from 'zod';
import { cn } from '@hvac/ui';
import { useQueryClient } from '@tanstack/react-query';
import { apiClient, setActiveOrganization } from '../../shared/api/queries.js';
import { useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * ⌘K.
 *
 * The brief's rule is that users should search more than they navigate, and
 * this is where that becomes true: one keystroke reaches every page, every
 * supplier, every bookkeeping record, and, when the query is not a thing but
 * a question, hands off to the Ask engine on the Pulse.
 *
 * Deliberately dependency-free. A palette is an input, a ranked list and four
 * key bindings; a library would bring its own opinions about all three.
 *
 * What it will never do: compute an answer. A query that looks like a question
 * routes to the six-intent Ask flow, where SQL does the arithmetic. The
 * palette is a door, not an oracle.
 */

interface PaletteItem {
  readonly id: string;
  readonly group: 'Go to' | 'Suppliers' | 'Customers' | 'Records' | 'Ask';
  readonly title: string;
  readonly hint: string | null;
  readonly run: () => void;
}

const PAGES: readonly { title: string; hint: string; to: string }[] = [
  { title: 'Pulse', hint: 'What matters today', to: '/' },
  { title: 'Everything', hint: 'Query every object; save collections', to: '/everything' },
  { title: 'Clients', hint: 'Mission control, every business at once', to: '/clients' },
  { title: 'Timeline', hint: 'Everything that happened, ⌘T', to: '/timeline' },
  { title: 'Review', hint: 'Documents waiting on a person', to: '/review' },
  { title: 'Bookkeeping', hint: 'Records and the queue', to: '/bookkeeping' },
  { title: 'Inbox', hint: 'Drop anything; the system sorts it', to: '/documents' },
  { title: 'Suppliers', hint: 'Who you buy from', to: '/suppliers' },
  { title: 'Customers', hint: 'Who you work for', to: '/customers' },
  { title: 'Jobs', hint: 'The unit of profit', to: '/jobs' },
  { title: 'Equipment', hint: 'Serials, models, warranty clocks', to: '/equipment' },
  { title: 'Bank', hint: 'Statements and reconciliation', to: '/bank' },
  { title: 'Tax', hint: 'Credits and the rules behind them', to: '/tax' },
  { title: 'Reports', hint: 'What changed, and whether to care', to: '/reports' },
  { title: 'Books', hint: 'Opening balances, GIFI codes, the T2 statements', to: '/books' },
  { title: 'Export', hint: 'Files for your accountant', to: '/export' },
  { title: 'Add a business', hint: 'A new client, fully isolated', to: '/organizations/new' },
];

/**
 * Subsequence match with a preference for prefixes and word starts.
 * Hand-rolled because the whole requirement is thirty lines.
 */
function fuzzyScore(query: string, candidate: string): number {
  const q = query.toLowerCase();
  const c = candidate.toLowerCase();
  if (q.length === 0) return 1;
  if (c.startsWith(q)) return 100 - c.length;
  if (c.includes(q)) return 60 - c.indexOf(q);
  let qi = 0;
  for (const ch of c) {
    if (ch === q[qi]) qi += 1;
    if (qi === q.length) return 20;
  }
  return qi === q.length ? 20 : -1;
}

export function CommandPalette({
  organizationId,
  organizations = [],
}: {
  organizationId: string;
  /** Every membership; foreign clients are searched too and labelled. */
  organizations?: { id: string; name: string }[];
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(0);
  const [remote, setRemote] = useState<PaletteItem[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const close = useCallback(() => {
    setOpen(false);
    setQuery('');
    setRemote([]);
    setSelected(0);
  }, []);

  /* ---- the one global binding ---- */
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setOpen((current) => !current);
      }
      // ⌘T: the whole business, chronologically. One keystroke away, like ⌘K.
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 't') {
        event.preventDefault();
        void navigate({ to: '/timeline' });
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [navigate]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  /* ---- server-side matches, debounced ----
   *
   * The empty-query reset lives inside the debounce timer too, so the effect
   * never calls setState synchronously during render commit, the same rule
   * the review screen follows, for the same cascading-render reason.
   */
  useEffect(() => {
    const term = query.trim();
    if (!open || term.length < 2) {
      const timer = setTimeout(() => setRemote([]), 0);
      return () => clearTimeout(timer);
    }
    let cancelled = false as boolean;
    const timer = setTimeout(() => {
      void (async () => {
        const go = (to: string, params?: Record<string, string>) => () => {
          void navigate({ to, params: params as never });
          close();
        };
        const found: PaletteItem[] = [];
        try {
          // The search spine, across every client the user can read. The firm
          // aggregates by asking each tenant separately, same endpoints, same
          // per-organization authorization; a reading arrangement, not an
          // access level. Active client first so its hits rank on top.
          const searchOrgs = [
            { id: organizationId, name: '' },
            ...organizations.filter((entry) => entry.id !== organizationId).slice(0, 6),
          ];
          const perOrg = await Promise.all(
            searchOrgs.map((org) =>
              queryClient
                .fetchQuery({
                  queryKey: ['search', org.id, term],
                  queryFn: ({ signal }) =>
                    apiClient.request({
                      path: `${API_ROUTES.search.query(org.id)}?q=${encodeURIComponent(term)}`,
                      schema: z.array(searchHitSchema),
                      signal,
                    }),
                  staleTime: 10_000,
                })
                .then((hits) => ({ org, hits }))
                .catch(() => ({ org, hits: [] as SearchHit[] })),
            ),
          );

          const destination = (
            hit: SearchHit,
          ): { to: string; params?: Record<string, string> } | null => {
            switch (hit.entityType) {
              case 'vendor':
                return { to: '/suppliers/$vendorId', params: { vendorId: hit.entityId } };
              case 'transaction':
                return { to: '/bookkeeping/$recordId', params: { recordId: hit.entityId } };
              case 'document':
                return { to: '/documents/$documentId', params: { documentId: hit.entityId } };
              case 'customer':
                return { to: '/customers/$customerId', params: { customerId: hit.entityId } };
              case 'job':
                return { to: '/jobs/$jobId', params: { jobId: hit.entityId } };
              case 'equipment':
                return { to: '/equipment/$equipmentId', params: { equipmentId: hit.entityId } };
              case 'note':
                // No page yet; offering a dead link would be worse than
                // omitting the hit.
                return null;
            }
          };

          const GROUPS: Partial<Record<SearchHit['entityType'], PaletteItem['group']>> = {
            vendor: 'Suppliers',
            customer: 'Customers',
            job: 'Records',
            equipment: 'Records',
            transaction: 'Records',
            document: 'Records',
          };

          for (const { org, hits } of perOrg) {
            const foreign = org.id !== organizationId;
            for (const hit of hits) {
              const target = destination(hit);
              const group = GROUPS[hit.entityType];
              if (target === null || group === undefined) continue;
              found.push({
                id: `${org.id}:${hit.entityType}:${hit.entityId}`,
                group,
                title: hit.title ?? 'Untitled',
                hint:
                  [
                    foreign ? org.name : null,
                    hit.occurredOn,
                    hit.amountMinor === null ? null : formatMinor(hit.amountMinor),
                  ]
                    .filter((part) => part !== null)
                    .join(' · ') || null,
                run: foreign
                  ? () => {
                      // A foreign hit switches the whole shell to that client
                      // first, every panel on the target page must load under
                      // the right tenant, so a clean reload is the honest move.
                      setActiveOrganization(org.id);
                      let path = target.to;
                      for (const [param, value] of Object.entries(target.params ?? {})) {
                        path = path.replace(`$${param}`, value);
                      }
                      window.location.assign(path);
                    }
                  : go(target.to, target.params),
              });
            }
          }
        } catch {
          // Search degrading to navigation is fine; failing loudly here would
          // punish typing.
        }
        if (!cancelled) {
          setRemote(found);
          setSelected(0);
        }
      })();
    }, 160);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [open, query, organizationId, queryClient, navigate, close]);

  /* ---- assemble + rank ---- */
  const items = useMemo<PaletteItem[]>(() => {
    const go = (to: string) => () => {
      void navigate({ to });
      close();
    };
    const pages = PAGES.map((page) => ({
      page,
      score: Math.max(fuzzyScore(query, page.title), fuzzyScore(query, page.hint) - 10),
    }))
      .filter((entry) => entry.score >= 0)
      .sort((a, b) => b.score - a.score)
      .map((entry): PaletteItem => ({
        id: `page:${entry.page.to}`,
        group: 'Go to',
        title: entry.page.title,
        hint: entry.page.hint,
        run: go(entry.page.to),
      }));

    const all = [...pages, ...remote];

    // A query with words in it is probably a question. Offer the Ask engine
    // last, always, the door to the one place that can actually answer.
    if (query.trim().length >= 3) {
      all.push({
        id: 'ask',
        group: 'Ask',
        title: `Ask the books: “${query.trim()}”`,
        hint: 'Computed from confirmed records, never guessed',
        run: () => {
          void navigate({ to: '/', search: { ask: query.trim() } as never });
          close();
        },
      });
    }
    return all;
  }, [query, remote, navigate, close]);

  /* ---- palette-local keys ---- */
  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      close();
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      setSelected((current) => Math.min(current + 1, items.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setSelected((current) => Math.max(current - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      items[selected]?.run();
    }
  };

  if (!open) return null;

  let lastGroup: string | null = null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-content/20 p-4 pt-[12vh] backdrop-blur-[2px]"
      onMouseDown={close}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Search"
        className="w-full max-w-xl overflow-hidden rounded-[var(--radius-overlay)] border border-border bg-surface shadow-[var(--shadow-overlay)]"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <input
          ref={inputRef}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Search pages, suppliers, records, or ask a question"
          aria-label="Search everything"
          className="w-full border-b border-border bg-transparent px-5 py-4 text-base text-content outline-none placeholder:text-content-subtle"
        />
        <ul className="max-h-[50vh] overflow-y-auto py-2" role="listbox">
          {items.length === 0 ? (
            <li className="px-5 py-6 text-sm text-content-muted">
              Nothing matches. Try a supplier name, an amount, or a question.
            </li>
          ) : (
            items.map((item, index) => {
              const heading = item.group !== lastGroup ? item.group : null;
              lastGroup = item.group;
              return (
                <li key={item.id}>
                  {heading === null ? null : (
                    <p className="px-5 pb-1 pt-3 text-xs font-semibold uppercase tracking-wide text-content-subtle">
                      {heading}
                    </p>
                  )}
                  <button
                    type="button"
                    role="option"
                    aria-selected={index === selected}
                    onMouseEnter={() => setSelected(index)}
                    onClick={item.run}
                    className={cn(
                      'flex w-full items-baseline justify-between gap-4 px-5 py-2.5 text-left',
                      index === selected ? 'bg-surface-sunken' : '',
                    )}
                  >
                    <span className="truncate text-sm text-content">{item.title}</span>
                    {item.hint === null ? null : (
                      <span className="shrink-0 text-xs text-content-subtle">{item.hint}</span>
                    )}
                  </button>
                </li>
              );
            })
          )}
        </ul>
        <div className="flex items-center gap-4 border-t border-border px-5 py-2 text-xs text-content-subtle">
          <span>↑↓ move</span>
          <span>↵ open</span>
          <span>esc close</span>
        </div>
      </div>
    </div>
  );
}
